'use client'

import React from 'react'
import { useCheckout } from '@/lib/hooks'
import { cn } from '@/lib/utils'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Input,
  LoadingDots,
} from '@/components/ui'

interface VoidOrderProps extends React.ComponentPropsWithoutRef<'div'> {
  orderId: string
  callback?: () => void
}

export const VoidOrder = ({ orderId, callback }: VoidOrderProps) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const { voidOrder } = useCheckout()
  const [isVoiding, setIsVoiding] = React.useState(false)
  const [reason, setReason] = React.useState('')

  const handleVoid = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    setIsVoiding(true)
    voidOrder({ orderId, reason })
    setIsVoiding(false)
    setIsOpen(false)
    callback?.()
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="destructive"
          className={cn('hidden gap-2 shadow-card lg:inline-flex')}
          size="sm"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            setIsOpen(true)
          }}
        >
          <span>Void Order</span>
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Void Order?</AlertDialogTitle>
          <AlertDialogDescription>
            You are about to void the selected order. This action cannot be
            undone. Are you sure you want to proceed?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex flex-col gap-2">
          <Input
            type="text"
            placeholder="Reason"
            value={reason}
            onChangeValue={(value) => setReason(value)}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel className="w-full">Close</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={handleVoid}
            className="w-full"
            autoFocus
            disabled={isVoiding}
          >
            {isVoiding ? <LoadingDots className="h-4 w-4" /> : 'Void Order'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
